import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeBackendApiBase } from '../utils/backendBaseUrl';
import './UserAuth.css';

const LS_BACKEND_APP = 'stoppuhr_backend';
const LS_BACKEND_USERAUTH = 'backendUrl';

/** Wie App.jsx (Login) bzw. UserAuth — oft leer beim ersten Aufruf per E-Mail-Link. */
function resolveBackendUrl(prop) {
  const p = (prop && String(prop).trim()) || '';
  if (p) return p;
  if (typeof window === 'undefined') return '';
  return (
    (localStorage.getItem(LS_BACKEND_APP) || '').trim() ||
    (localStorage.getItem(LS_BACKEND_USERAUTH) || '').trim()
  );
}

/**
 * Passwort vergessen — Endpoints wie backend/src/handlers/user/password_reset.rs
 */
export default function PasswordReset({ backendUrl, initialToken, onCancel, onSuccess }) {
  const { t } = useTranslation();
  const [apiRootInput, setApiRootInput] = useState(() =>
    typeof window !== 'undefined' ? resolveBackendUrl('') : ''
  );
  const [step, setStep] = useState(initialToken ? 'confirm' : 'request');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState(initialToken || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const r = resolveBackendUrl(backendUrl || '');
    if (r) setApiRootInput(r);
  }, [backendUrl]);

  useEffect(() => {
    if (initialToken) {
      setToken(initialToken);
      setStep('confirm');
    }
  }, [initialToken]);

  const base = useMemo(() => normalizeBackendApiBase(apiRootInput.trim()), [apiRootInput]);

  const networkErrorHint = useCallback(
    (err, apiBase) => {
      const m = (err && err.message) || String(err);
      if (/Failed to fetch|NetworkError|Load failed|fetch|ERR_SSL|SSL_PROTOCOL/i.test(m)) {
        let msg = t('password_reset.errors.backend_connection_failed');
        if (
          typeof window !== 'undefined' &&
          window.location.protocol === 'https:' &&
          apiBase &&
          /^http:\/\//i.test(apiBase)
        ) {
          msg += ` ${t('password_reset.errors.mixed_content_hint')}`;
        }
        return msg;
      }
      return m || t('password_reset.errors.network_error');
    },
    [t]
  );

  const persistBackendRoot = useCallback((root) => {
    try {
      localStorage.setItem(LS_BACKEND_APP, root.trim());
    } catch {
      /* ignore */
    }
  }, []);

  const requestReset = useCallback(
    async (e) => {
      e.preventDefault();
      setError('');
      setInfo('');
      const root = apiRootInput.trim();
      if (!root) {
        setError(t('password_reset.errors.backend_url_required_long'));
        return;
      }
      const apiBase = normalizeBackendApiBase(root);
      if (!apiBase) {
        setError(t('password_reset.errors.backend_url_invalid'));
        return;
      }
      setLoading(true);
      try {
        const res = await fetch(`${apiBase}/api/user/password-reset/request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim() }),
          mode: 'cors',
          credentials: 'omit',
        });
        const data = await res.json().catch(() => ({}));
        if (res.status === 429) {
          setError(data.message || t('password_reset.errors.too_many_requests'));
          return;
        }
        if (!res.ok) {
          setError(data.message || data.error || t('password_reset.errors.request_failed'));
          return;
        }
        persistBackendRoot(root);
        setInfo(data.message || t('password_reset.info.request_success'));
        setStep('afterRequest');
      } catch (err) {
        setError(networkErrorHint(err, apiBase));
      } finally {
        setLoading(false);
      }
    },
    [apiRootInput, email, persistBackendRoot, networkErrorHint, t]
  );

  const confirmReset = useCallback(
    async (e) => {
      e.preventDefault();
      setError('');
      setInfo('');
      if (newPassword.length < 8) {
        setError(t('password_reset.errors.password_min_length'));
        return;
      }
      if (newPassword !== newPassword2) {
        setError(t('password_reset.errors.passwords_mismatch'));
        return;
      }
      const root = apiRootInput.trim();
      if (!root) {
        setError(t('password_reset.errors.backend_url_required_short'));
        return;
      }
      const apiBase = normalizeBackendApiBase(root);
      if (!apiBase) {
        setError(t('password_reset.errors.backend_url_invalid'));
        return;
      }
      const trimmedCode = code.replace(/\D/g, '');
      setLoading(true);
      try {
        const body = {
          token: token.trim(),
          new_password: newPassword,
          ...(trimmedCode.length === 6 ? { code: trimmedCode } : {}),
        };
        const res = await fetch(`${apiBase}/api/user/password-reset/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          mode: 'cors',
          credentials: 'omit',
        });
        const data = await res.json().catch(() => ({}));
        if (res.status === 429) {
          setError(data.message || t('password_reset.errors.too_many_attempts'));
          return;
        }
        if (!res.ok) {
          setError(data.message || data.error || t('password_reset.errors.confirm_failed'));
          return;
        }
        persistBackendRoot(root);
        setInfo(data.message || t('password_reset.info.confirm_success'));
        if (onSuccess) onSuccess();
      } catch (err) {
        setError(networkErrorHint(err, apiBase));
      } finally {
        setLoading(false);
      }
    },
    [apiRootInput, token, code, newPassword, newPassword2, onSuccess, persistBackendRoot, networkErrorHint, t]
  );

  return (
    <div className="user-auth-container">
      <div className="user-auth-card user-auth-card--password-reset">
        <header className="password-reset-header">
          <div className="password-reset-header__side">
            {onCancel ? (
              <button type="button" className="password-reset-back-btn" onClick={onCancel}>
                {t('password_reset.buttons.back')}
              </button>
            ) : null}
          </div>
          <h1 className="password-reset-header__title">{t('password_reset.title')}</h1>
          <div className="password-reset-header__side password-reset-header__side--end" aria-hidden="true" />
        </header>
        <p className="auth-intro">
          {t('password_reset.intro_before')}{' '}
          <code>/api/user/password-reset/request</code> {t('password_reset.intro_middle')}{' '}
          <code>…/confirm</code> {t('password_reset.intro_after')}
        </p>

        <div className="form-group" style={{ marginBottom: '8px' }}>
          <label htmlFor="pr-backend-root">
            {t('password_reset.backend_url.label_before')} <code>/api</code>{' '}
            {t('password_reset.backend_url.label_after')}
          </label>
          <input
            id="pr-backend-root"
            type="url"
            value={apiRootInput}
            onChange={(e) => setApiRootInput(e.target.value)}
            disabled={loading}
            placeholder={t('password_reset.backend_url.placeholder')}
            autoComplete="url"
          />
          <small className="auth-hint">
            {t('password_reset.backend_url.hint')}
          </small>
        </div>
        {base ? (
          <p className="auth-api-line">
            {t('password_reset.backend_url.call_prefix')} <code>{base}/api/user/password-reset/…</code>
          </p>
        ) : null}

        {error && <div className="auth-error">{error}</div>}
        {info && step !== 'afterRequest' && <div className="auth-success">{info}</div>}

        {step === 'request' && (
          <form onSubmit={requestReset}>
            <div className="form-group">
              <label htmlFor="pr-email">{t('password_reset.request.email_label')}</label>
              <input
                id="pr-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoComplete="email"
                placeholder={t('password_reset.request.email_placeholder')}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('password_reset.buttons.sending') : t('password_reset.buttons.request_link_and_code')}
            </button>
          </form>
        )}

        {step === 'afterRequest' && (
          <div>
            <div className="auth-success" style={{ marginBottom: '16px' }}>
              {info}
            </div>
            <button type="button" className="btn btn-primary" onClick={() => setStep('confirm')}>
              {t('password_reset.buttons.set_new_password')}
            </button>
          </div>
        )}

        {step === 'confirm' && (
          <form onSubmit={confirmReset}>
            {!initialToken && (
              <div className="form-group">
                <label htmlFor="pr-token">{t('password_reset.confirm.token_label')}</label>
                <input
                  id="pr-token"
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="off"
                  placeholder={t('password_reset.confirm.token_placeholder')}
                />
              </div>
            )}
            <div className="form-group">
              <label htmlFor="pr-code">{t('password_reset.confirm.code_label')}</label>
              <input
                id="pr-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={loading}
                placeholder={t('password_reset.confirm.code_placeholder')}
                aria-describedby="pr-code-hint"
              />
              <small id="pr-code-hint" className="auth-hint">
                {t('password_reset.confirm.code_hint')}
              </small>
            </div>
            <div className="form-group">
              <label htmlFor="pr-np">{t('password_reset.confirm.new_password_label')}</label>
              <input
                id="pr-np"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                disabled={loading}
                autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <label htmlFor="pr-np2">{t('password_reset.confirm.new_password_repeat_label')}</label>
              <input
                id="pr-np2"
                type="password"
                value={newPassword2}
                onChange={(e) => setNewPassword2(e.target.value)}
                required
                minLength={8}
                disabled={loading}
                autoComplete="new-password"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('password_reset.buttons.saving') : t('password_reset.buttons.save_new_password')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
